import styles from "./OrganisationEventDetail.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AlertContext } from "../context/AlertContext";
import { AuthContext } from "../context/AuthContext";
import { fetcher, isFetcherError } from "../utils/fetcher";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Tab,
  TextField,
} from "@mui/material";
import classNames from "classnames";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateTimePicker } from "@mui/x-date-pickers";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import { CancelEventDialog } from "../components/CancelEventDialog/CancelEventDialog";
import { AttendeesTable } from "../components/AttendeesTable/AttendeesTable";
import { GalleryModal } from "../components/GalleryModal/GalleryModal";
import { EventInterestsSkillsSection } from "../components/EventInterestsSkillsSection/EventInterestsSkillsSection";
import { VolunteerRatingDialog } from "../components/VolunteerRatingDialog/VolunteerRatingDialog";
import { useEnumStore } from "../store/enumStore";
import { EventOwnData } from "../types";
import { format } from "date-fns";
import { cs } from "date-fns/locale";

interface EventImage {
  id: string;
  public_link: string;
}

interface EventDocument {
  id: string;
  name: string;
  public_link: string;
}

interface OrganisationDocument {
  id: string;
  name: string;
  public_link: string;
  type: string;
}

interface GalleryState {
  modalOpen: boolean;
  loading: boolean;
  images: EventImage[];
}

const ACTIVE_PARTICIPANT_STATUSES = [
  "applied",
  "approved",
  "invited",
  "invitation_accepted",
];

const formatParticipantDate = (value?: string | null) => {
  if (!value) return "N/A";

  const normalizedValue = value.includes(" ") ? value.replace(" ", "T") : value;
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return "N/A";
  }

  return format(parsedDate, "d. MMMM yyyy", { locale: cs });
};

export const OrganisationEventDetail = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const alertContext = useContext(AlertContext);
  const authContext = useContext(AuthContext);
  const skills = useEnumStore((state) => state.skills);
  const interests = useEnumStore((state) => state.interests);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const setAlert = alertContext?.setAlert ?? (() => {});
  const currentUser = authContext?.currentUser;

  const [tab, setTab] = useState<string>("1");
  const [event, setEvent] = useState<EventOwnData | null>(null);
  const [editedEvent, setEditedEvent] = useState<EventOwnData | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [editMode, setEditMode] = useState<string>("");
  const [galleryOpen, setGalleryOpen] = useState<boolean>(false);
  const [galleryIndex, setGalleryIndex] = useState<number>(0);
  const [dialogOpen, setDialogOpen] = useState<string>("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState<boolean>(false);
  const [ratingSubmitting, setRatingSubmitting] = useState<boolean>(false);
  const [selectedMatchForRating, setSelectedMatchForRating] = useState<{
    matchId: string;
    volunteerName: string;
    initialScore: number;
    initialShowUp: boolean;
  } | null>(null);
  const [message, setMessage] = useState<string>("");
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);
  const [exportingDocs, setExportingDocs] = useState<boolean>(false);

  const [gallery, setGallery] = useState<GalleryState>({
    modalOpen: false,
    loading: false,
    images: [],
  });
  const [selectedImages, setSelectedImages] = useState<EventImage[]>([]);
  const [eventsContainingImage, setEventsContainingImage] = useState<any[]>([]);
  const [documents, setDocuments] = useState<OrganisationDocument[] | null>(
    null,
  );
  const [documentsLoading, setDocumentsLoading] = useState<boolean>(false);

  const organisation_id = currentUser?.userId;
  const maxUploadingImages = 30 - gallery.images?.length;

  const applyEventDetailState = (eventDetail: EventOwnData) => {
    setEvent(eventDetail);
    const editedEventWithStrings = {
      ...eventDetail,
      skills: (eventDetail.skills || []).map((id: number) => String(id)),
      interests: (eventDetail.interests || []).map((id: number) => String(id)),
      documentation: (((eventDetail as any).documentation || []) as any[])
        .map((doc: any) => (typeof doc === "string" ? doc : doc.id))
        .map((id: string | number) => String(id)),
    };
    setEditedEvent(editedEventWithStrings as any);
    setSelectedImages((eventDetail.event_image as EventImage[]) || []);
    setDateFrom(
      eventDetail.event_start_datetime
        ? new Date(eventDetail.event_start_datetime)
        : null,
    );
    setDateTo(
      eventDetail.event_end_datetime
        ? new Date(eventDetail.event_end_datetime)
        : null,
    );
  };

  const fetchEventDetail = async () => {
    if (!eventId) return;

    try {
      setLoading(true);
      setError(null);
      const eventDetail = await fetcher<EventOwnData>({
        url: `/event/${eventId}`,
      });
      applyEventDetailState(eventDetail);
    } catch (error) {
      console.error("Error fetching event detail:", error);
      setError(
        "Informace o události nebo pořadateli nejsou dostupné, zkuste to prosím později.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgsGallery = async () => {
    if (!organisation_id) return;

    try {
      setGallery((prevState) => ({ ...prevState, loading: true }));
      const data = await fetcher<EventImage[]>({
        url: `/organisation/get-organisation-images/${organisation_id}`,
      });
      setGallery((prevState) => ({ ...prevState, images: data || [] }));
    } catch (error) {
      console.error("Error fetching organisation's images:", error);
      setAlert(true, "error", "Nepodařilo se načíst obrázky organizace.");
    } finally {
      setGallery((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const fetchOrgsDocuments = async () => {
    if (!currentUser?.userId) return;

    try {
      setDocumentsLoading(true);
      const data = await fetcher<OrganisationDocument[]>({
        url: `/organisation/get-documents/${currentUser.userId}`,
      });
      setDocuments(data || []);
    } catch (error) {
      console.error("Error fetching organisation's documents:", error);
      setAlert(true, "error", "Nepodařilo se načíst dokumenty organizace.");
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    if (!eventId) {
      setError("Událost nebyla nalezena.");
      setLoading(false);
      return;
    }

    fetchEventDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (organisation_id) {
      fetchOrgsGallery();
      fetchOrgsDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation_id]);

  const handleCheckboxToggle = (
    value: string,
    field: "skills" | "interests" | "documentation",
  ) => {
    if (!editedEvent) return;

    const strValue = String(value);
    const currentField = ((editedEvent as any)[field] || []) as (
      | string
      | number
    )[];
    const currentFieldStrings = currentField.map((id) => String(id));
    const alreadyExists = currentFieldStrings.includes(strValue);
    if (alreadyExists) {
      setEditedEvent({
        ...editedEvent,
        [field]: currentFieldStrings.filter((item) => item !== strValue),
      } as any);
    } else {
      setEditedEvent({
        ...editedEvent,
        [field]: [...currentFieldStrings, strValue],
      } as any);
    }
  };

  const isImageSelected = (imageId: string) => {
    return selectedImages.some((img) => img.id === imageId);
  };

  const handleImageUpload = async (files: File[]) => {
    if (!organisation_id) return;

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      await fetcher<void>({
        url: `/organisation/upload-image-to-gallery/${organisation_id}`,
        method: "POST",
        body: formData,
        noContentType: true,
      });
      await fetchOrgsGallery();
      setAlert(true, "success", "Nahrání obrázků proběhlo úspěšně.");
    } catch (error) {
      setAlert(
        true,
        "error",
        "Nahrání obrázků se nezdařilo, zkuste to prosím znovu později.",
      );
      console.error("Error uploading images:", error);
    }
  };

  const handleImageSelect = async (image: EventImage) => {
    const alreadyExists = isImageSelected(image.id);
    if (alreadyExists) {
      setSelectedImages((prevState) => [
        ...prevState.filter((img) => img.id !== image.id),
      ]);
    } else setSelectedImages((prevState) => [...prevState, image]);
  };

  const handleCheckImageInEvents = async (image: EventImage) => {
    if (!organisation_id) return;

    try {
      const data = await fetcher<any[]>({
        url: `/event/image-related-events/${organisation_id}?image_id=${image.id}`,
      });
      setEventsContainingImage(data || []);
    } catch (error) {
      setAlert(
        true,
        "error",
        "Smazání obrázku se nezdařilo, protože nebylo možné ověřit propojení tohoto obrázku s událostmi, zkuste to prosím znovu později.",
      );
      console.error("Error checking image in events:", error);
    }
  };

  const handleImageDelete = async (image: EventImage) => {
    if (!organisation_id) return;

    try {
      await fetcher<void>({
        url: `/organisation/delete-from-image-gallery/${organisation_id}?image_id=${image.id}`,
        method: "DELETE",
      });
      await fetchOrgsGallery();
      setAlert(true, "success", "Smazání obrázku proběhlo úspěšně.");
    } catch (error) {
      setAlert(
        true,
        "error",
        "Smazání obrázku se nezdařilo, zkuste to prosím znovu později.",
      );
      console.error("Error deleting image:", error);
    }
  };

  const handleOpenGallery = () => {
    setGallery((prevState) => ({ ...prevState, modalOpen: true }));
  };

  const parseErrorDetail = (body?: string): string | undefined => {
    if (!body) return undefined;
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed.detail === "string") {
        return parsed.detail;
      }
    } catch {
      return undefined;
    }
    return undefined;
  };

  const handleOpenRatingDialog = (
    matchId: string,
    volunteerName: string,
    initialScore = 5,
    initialShowUp = true,
  ) => {
    setSelectedMatchForRating({
      matchId,
      volunteerName,
      initialScore: Math.min(5, Math.max(1, Math.round(initialScore))),
      initialShowUp,
    });
    setRatingDialogOpen(true);
  };

  const handleCloseRatingDialog = () => {
    if (ratingSubmitting) return;
    setRatingDialogOpen(false);
    setSelectedMatchForRating(null);
  };

  const handleSubmitRating = async (
    selectedScore: number,
    selectedDidShowUp: boolean,
  ) => {
    if (!selectedMatchForRating) return;

    try {
      setRatingSubmitting(true);
      const safeScore = Math.min(5, Math.max(1, Math.round(selectedScore)));
      const query = new URLSearchParams({
        rating_score: String(safeScore),
        volunteer_did_show_up: String(selectedDidShowUp),
      });

      await fetcher<void>({
        url: `/match/rating/${selectedMatchForRating.matchId}?${query.toString()}`,
        method: "PUT",
      });

      await fetchEventDetail();
      setAlert(true, "success", "Hodnocení dobrovolníka bylo uloženo.");
      handleCloseRatingDialog();
    } catch (error) {
      console.error("Error rating volunteer:", error);

      if (isFetcherError(error)) {
        const detail = parseErrorDetail(error.body);
        if (detail) {
          setAlert(true, "warning", detail);
        } else {
          setAlert(
            true,
            "error",
            "Hodnocení se nepodařilo uložit. Zkuste to prosím později.",
          );
        }
      } else {
        setAlert(
          true,
          "error",
          "Hodnocení se nepodařilo uložit. Zkuste to prosím později.",
        );
      }
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleCloseGallery = () => {
    setGallery((prevState) => ({ ...prevState, modalOpen: false }));
  };

  const handleAssignImages = async () => {
    setEditedEvent((prevState) => ({
      ...prevState!,
      event_image: selectedImages as any,
    }));
    await handleEditConfirm("event_image", selectedImages);
  };

  const handleEdit = (section: string) => {
    if (!event || !editedEvent) return;

    if (section === "images") {
      handleOpenGallery();
      return;
    }

    if (section === "info") {
      setEditedEvent({
        ...event,
        location: (editedEvent as any).location,
        capacity: editedEvent.capacity,
        details: editedEvent.details,
      });
    } else {
      setEditedEvent({
        ...event,
        [section]: (editedEvent as any)?.[section],
      });
    }

    setEditMode(section);
    if (section !== "info" && section !== "documentation") {
      setDialogOpen(section);
    }
  };

  const handleEditCancel = (section: string) => {
    if (!event || !editedEvent) return;

    if (section === "info") {
      setEditedEvent({
        ...editedEvent,
        location: (event as any).location,
        capacity: event.capacity,
        details: event.details,
      });
      setDateFrom(
        event.event_start_datetime
          ? new Date(event.event_start_datetime)
          : null,
      );
      setDateTo(
        event.event_end_datetime ? new Date(event.event_end_datetime) : null,
      );
    } else if (section === "documentation") {
      setEditedEvent({
        ...editedEvent,
        documentation: (((event as any).documentation || []) as any[])
          .map((doc: any) => (typeof doc === "string" ? doc : doc.id))
          .map((id: string | number) => String(id)),
      } as any);
    } else {
      setEditedEvent({
        ...editedEvent,
        [section]: (event as any)?.[section],
      });
    }

    setEditMode("");
    if (dialogOpen !== "") {
      setDialogOpen("");
    }
  };

  const handleEditConfirm = async (
    section: string,
    selectedImages?: EventImage[],
  ) => {
    if (!event || !editedEvent) return;

    try {
      let updatedEvent: EventOwnData;

      if (section === "info") {
        const startDate = dateFrom
          ? new Date(dateFrom)
          : new Date(event.event_start_datetime!);
        const endDate = dateTo
          ? new Date(dateTo)
          : new Date(event.event_end_datetime!);

        updatedEvent = {
          ...event,
          location: (editedEvent as any).location,
          capacity: editedEvent.capacity!,
          details: editedEvent.details!,
          event_start_datetime: startDate.toISOString(),
          event_end_datetime: endDate.toISOString(),
        };
      } else if (section === "event_image" && selectedImages) {
        updatedEvent = {
          ...event,
          [section]: selectedImages,
        };
      } else if (section === "documentation") {
        const selectedDocIds = (
          ((editedEvent as any).documentation || []) as (string | number)[]
        ).map((id) => String(id));
        const selectedDocs = (documents || []).filter((doc) =>
          selectedDocIds.includes(String(doc.id)),
        );
        updatedEvent = {
          ...event,
          documentation: selectedDocs as any,
        } as any;
      } else {
        updatedEvent = {
          ...event,
          [section]: (editedEvent as any)?.[section],
        };
      }

      const formatDate = (date: string | Date | null | undefined) => {
        if (!date) return null;
        if (typeof date === "string") {
          return new Date(date).toISOString();
        }
        if (date instanceof Date) {
          return date.toISOString();
        }
        return new Date(date).toISOString();
      };

      const updatePayload = {
        event_title: updatedEvent.event_title,
        event_type: updatedEvent.event_type,
        // Convert to strings if they're numbers (from API), backend expects strings
        skills: ((updatedEvent.skills || []) as any[]).map(
          (id: string | number) => String(id),
        ),
        interests: ((updatedEvent.interests || []) as any[]).map(
          (id: string | number) => String(id),
        ),
        event_start_datetime: formatDate(updatedEvent.event_start_datetime),
        event_end_datetime: formatDate(updatedEvent.event_end_datetime),
        address: updatedEvent.address || (updatedEvent as any).location || "",
        details: updatedEvent.details || "",
        documentation: ((updatedEvent as any).documentation || []).map(
          (doc: any) => (typeof doc === "string" ? doc : doc.id),
        ),
        capacity: parseInt((updatedEvent.capacity || 15).toString()),
        is_urgent: Boolean((updatedEvent as any).is_urgent),
        event_image: updatedEvent.event_image || [],
      };

      await fetcher<void>({
        url: `/event/${eventId}`,
        method: "PUT",
        body: JSON.stringify(updatePayload),
      });

      await fetchEventDetail();
      setAlert(true, "success", "Změny byly úspěšně uloženy.");
    } catch (error) {
      console.error("Error updating event:", error);
      setAlert(
        true,
        "error",
        "Nepodařilo se uložit změny. Zkuste to prosím znovu.",
      );
      return;
    }

    setEditMode("");
    if (dialogOpen !== "") {
      setDialogOpen("");
    }
  };

  const handleExportDocs = async (selectedIds: string[]) => {
    if (!eventId || selectedIds.length === 0) return;

    setExportingDocs(true);
    try {
      const params = new URLSearchParams({ event_id: eventId });
      selectedIds.forEach((id) => params.append("volunteer_id", id));

      const blob = await fetcher<Blob>({
        url: `/export_filled_docs?${params.toString()}`,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dokumenty_${eventId}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setAlert(true, "success", "Dokumenty byly úspěšně staženy.");
    } catch (error) {
      console.error("Error exporting documents:", error);
      setAlert(
        true,
        "error",
        "Chyba při stahování dokumentů. Zkuste to prosím znovu.",
      );
    } finally {
      setExportingDocs(false);
    }
  };

  const handleSendMessage = async () => {
    if (!event) return;

    const activeMatchRecipients = Array.isArray(event.matches)
      ? event.matches.filter((match: any) =>
          ACTIVE_PARTICIPANT_STATUSES.includes(match?.status),
        )
      : [];
    const recipientsCount = Array.isArray(event.attendees)
      ? event.attendees.length
      : activeMatchRecipients.length;
    const trimmedMessage = message.trim();

    if (recipientsCount === 0) {
      setAlert(
        true,
        "warning",
        "Zprávu nelze odeslat, protože akce zatím nemá žádné účastníky.",
      );
      return;
    }

    if (!trimmedMessage) {
      setAlert(true, "warning", "Prosím zadejte zprávu před odesláním.");
      return;
    }

    if (trimmedMessage.length < 50 || trimmedMessage.length > 500) {
      setAlert(true, "warning", "Zpráva musí mít 50 až 500 znaků.");
      return;
    }

    setSendingMessage(true);
    try {
      await fetcher<void>({
        url: `/event/send-email/${event.id}`,
        method: "POST",
        body: JSON.stringify({ custom_message: trimmedMessage }),
      });

      setAlert(
        true,
        "success",
        "Zpráva byla úspěšně odeslána všem účastníkům.",
      );
      setMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      setAlert(
        true,
        "error",
        "Chyba při odesílání zprávy. Zkuste to prosím znovu.",
      );
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading)
    return (
      <div className={classNames(styles.eventDetail, styles.error)}>
        <CircularProgress />
      </div>
    );

  if (error)
    return (
      <div className={classNames(styles.eventDetail, styles.error)}>
        {error}
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={() => navigate("/organisation/events")}
        >
          Zpět na přehled
        </Button>
      </div>
    );

  if (!event || !editedEvent) {
    return (
      <div className={classNames(styles.eventDetail, styles.error)}>
        Informace o události nebo pořadateli nejsou dostupné, zkuste to prosím
        později.
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={() => navigate("/organisation/events")}
        >
          Zpět na přehled
        </Button>
      </div>
    );
  }

  const images =
    (Array.isArray(event?.event_image) ? event.event_image : [])?.map(
      (img: any) => ({
        src: img.public_link,
      }),
    ) || [];

  const activeMatches = Array.isArray(event.matches)
    ? event.matches.filter((match: any) =>
        ACTIVE_PARTICIPANT_STATUSES.includes(match?.status),
      )
    : [];
  const matchByVolunteerId = new Map<string, any>(
    activeMatches
      .filter((match: any) => typeof match?.volunteer_id === "string")
      .map((match: any) => [String(match.volunteer_id), match]),
  );
  const attendeesCount = Array.isArray(event.attendees)
    ? event.attendees.length
    : activeMatches.length;
  const eventDocuments = ((event as any).documentation || []) as (
    | EventDocument
    | string
  )[];
  const eventEndDate = event.event_end_datetime
    ? new Date(event.event_end_datetime)
    : null;
  const isEventEnded =
    !eventEndDate ||
    (!Number.isNaN(eventEndDate.getTime()) &&
      eventEndDate.getTime() <= Date.now());
  const minTextLength = 100;
  const remainingCharacters = Math.max(
    0,
    minTextLength - (editedEvent.details || "").length,
  );

  const attendees = Array.isArray(event.attendees)
    ? event.attendees.map((attendee) => {
        const attendeeMatch = matchByVolunteerId.get(
          String(attendee.volunteer_id),
        );
        const attendeeMatchId =
          typeof attendeeMatch?.id === "string" ? attendeeMatch.id : undefined;
        const initialScore =
          typeof attendeeMatch?.volunteer_rating === "number"
            ? attendeeMatch.volunteer_rating
            : typeof attendee.rating === "number"
              ? attendee.rating
              : 5;
        const initialShowUp =
          typeof attendeeMatch?.volunteer_did_show_up === "boolean"
            ? attendeeMatch.volunteer_did_show_up
            : typeof attendee.attended === "boolean"
              ? attendee.attended
              : true;

        return {
          id: attendee.volunteer_id,
          name: attendee.volunteer_name || "N/A",
          email: "N/A",
          date: formatParticipantDate(attendee.registered_at),
          status: "Připraveno k podepsání",
          action: !isEventEnded ? null : attendeeMatchId ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenRatingDialog(
                  attendeeMatchId,
                  attendee.volunteer_name || "dobrovolník",
                  initialScore,
                  initialShowUp,
                );
              }}
            >
              Ohodnotit
            </Button>
          ) : (
            <Button size="small" variant="outlined" disabled>
              Není dostupné
            </Button>
          ),
          profileImage: attendee.volunteer_picture_url,
        };
      })
    : activeMatches.map((match: any) => {
        const volunteer = match.volunteer?.[0];
        const fullName = volunteer
          ? `${volunteer.first_name || ""} ${volunteer.last_name || ""}`.trim()
          : "N/A";

        const registrationDate = formatParticipantDate(match.date_of_match);

        return {
          id: match.volunteer_id || match.id,
          name: fullName,
          email: volunteer?.email || "N/A",
          date: registrationDate,
          status: "Připraveno k podepsání",
          action: !isEventEnded ? null : typeof match.id === "string" ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenRatingDialog(
                  match.id,
                  fullName || "dobrovolník",
                  typeof match.volunteer_rating === "number"
                    ? match.volunteer_rating
                    : 5,
                  typeof match.volunteer_did_show_up === "boolean"
                    ? match.volunteer_did_show_up
                    : true,
                );
              }}
            >
              Ohodnotit
            </Button>
          ) : (
            <Button size="small" variant="outlined" disabled>
              Není dostupné
            </Button>
          ),
          profileImage: volunteer?.profile_image,
        };
      });
  const hasAttendees = attendees.length > 0;
  const trimmedMessage = message.trim();
  const messageLength = trimmedMessage.length;
  const isMessageLengthValid = messageLength >= 50 && messageLength <= 500;
  const isSendMessageDisabled =
    sendingMessage || !hasAttendees || !isMessageLengthValid;

  return (
    <>
      <div className={styles.eventDetail}>
        <TabContext value={tab}>
          <TabList
            onChange={(_e, value) => setTab(value)}
            aria-label="volunteer-profile-tabs"
            className={styles.tabList}
          >
            <Tab label="Základní údaje" value="1" className={styles.tab} />
            <Tab label="Účastníci" value="2" className={styles.tab} />
            <Tab label="Dokumenty" value="3" className={styles.tab} />
          </TabList>
          <div className={styles.separator}></div>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate("/organisation/events")}
          >
            <ArrowBackIosIcon />
            Zpět na přehled
          </Button>
          <TabPanel value="1" className={styles.content}>
            <div className={styles.eventIntro}>
              <p>Název akce</p>
              <h3>{event.event_title}</h3>
              <p className={styles.primaryText}>
                Přihlášených účastníků:{" "}
                <span>
                  {attendeesCount}/{event.capacity}
                </span>
              </p>
            </div>
            <div className={styles.flex}>
              <TextField
                id="location"
                type="text"
                label="Kde se akce koná"
                variant="outlined"
                size="small"
                value={(editedEvent as any).location || editedEvent.address}
                onChange={(e) =>
                  setEditedEvent({
                    ...editedEvent,
                    location: e.target.value,
                  } as any)
                }
                disabled={editMode !== "info"}
                className={styles.minWidth}
              />
              <DateTimePicker
                label="Termín od"
                views={["year", "day", "hours", "minutes"]}
                disablePast
                slotProps={{
                  textField: { size: "small" },
                }}
                value={dateFrom || undefined}
                onChange={(newValue) => setDateFrom(newValue)}
                disabled={editMode !== "info"}
              />
              <DateTimePicker
                label="Termín do"
                views={["year", "day", "hours", "minutes"]}
                disablePast
                minDateTime={dateFrom || undefined}
                slotProps={{
                  textField: { size: "small" },
                }}
                value={dateTo || undefined}
                onChange={(newValue) => setDateTo(newValue)}
                disabled={editMode !== "info"}
              />
              <TextField
                id="capacity"
                type="number"
                label="Kapacita akce"
                variant="outlined"
                size="small"
                value={editedEvent.capacity}
                onChange={(e) =>
                  setEditedEvent({
                    ...editedEvent,
                    capacity: parseInt(e.target.value),
                  })
                }
                disabled={editMode !== "info"}
              />
            </div>
            <div className={styles.eventDescription}>
              <TextField
                id="details"
                label="Popis akce"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={editedEvent.details || ""}
                onChange={(e) =>
                  setEditedEvent({
                    ...editedEvent,
                    details: e.target.value,
                  })
                }
                disabled={editMode !== "info"}
                helperText={
                  (editedEvent.details || "").length < minTextLength
                    ? `Zbývá zadat ${remainingCharacters} znaků.`
                    : "Minimální počet znaků splněn."
                }
              />
            </div>
            <div className={styles.flex}>
              {editMode === "info" && (
                <Button
                  variant="outlined"
                  onClick={() => handleEditCancel("info")}
                >
                  Zrušit
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() =>
                  editMode === "info"
                    ? handleEditConfirm(editMode)
                    : handleEdit("info")
                }
              >
                {editMode === "info" ? "Potvrdit" : "Upravit údaje"}
              </Button>
            </div>
            <div className={styles.skills}>
              <EventInterestsSkillsSection
                title="Zaměření akce"
                items={interests as any}
                selectedIds={(event.interests || []).map((id: number) =>
                  String(id),
                )}
                editMode={false}
                type="interests"
              />
              <Button
                variant="contained"
                onClick={() => handleEdit("interests")}
              >
                Upravit zaměření akce
              </Button>
            </div>
            <div className={styles.skills}>
              <EventInterestsSkillsSection
                title="Vhodné dovednosti pro akci"
                items={skills as any}
                selectedIds={(event.skills || []).map((id: number) =>
                  String(id),
                )}
                editMode={false}
                type="skills"
              />
              <Button variant="contained" onClick={() => handleEdit("skills")}>
                Upravit dovednosti
              </Button>
            </div>
            <div className={styles.gallery}>
              <h4>Fotografie k akci</h4>
              {images.length > 0 ? (
                <>
                  <div className={styles.images}>
                    {images.map((img: any, idx: number) => {
                      return (
                        <img
                          key={idx}
                          src={img.src}
                          alt="event-image"
                          width={150}
                          height={117}
                          onClick={() => {
                            setGalleryIndex(idx);
                            setGalleryOpen(true);
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      );
                    })}
                  </div>
                  <Lightbox
                    open={galleryOpen}
                    close={() => setGalleryOpen(false)}
                    index={galleryIndex}
                    slides={images}
                    plugins={[Thumbnails]}
                  />
                </>
              ) : (
                <p style={{ color: "#666", fontSize: "14px" }}>
                  K této akci zatím nebyly přidány žádné fotografie.
                </p>
              )}
              <Button
                variant="contained"
                onClick={() => handleEdit("images")}
                style={{ marginTop: "16px" }}
              >
                Upravit fotografie
              </Button>
            </div>
            <div className={styles.footer}>
              <Button
                color="error"
                variant="outlined"
                onClick={() => setCancelDialogOpen(true)}
              >
                Zrušit akci
              </Button>
            </div>
          </TabPanel>
          <TabPanel value="2" className={styles.content}>
            <Paper className={styles.box}>
              <AttendeesTable
                attendees={attendees}
                onExportDocs={handleExportDocs}
                isExportingDocs={exportingDocs}
              />
            </Paper>
            <div className={styles.messageWrapper}>
              <p>
                Poslat zprávu
                <span> všem {attendees.length} účastníkům</span>
              </p>
              <p>
                Zprávu lze odeslat pouze pokud má akce účastníky. Délka zprávy
                musí být 50 až 500 znaků.
              </p>
              <TextField
                id="message"
                label="Text zprávy"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                error={trimmedMessage.length > 0 && !isMessageLengthValid}
                helperText={`Počet znaků: ${messageLength}/500`}
                inputProps={{ maxLength: 500 }}
              />
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={isSendMessageDisabled}
                startIcon={
                  sendingMessage ? <CircularProgress size={16} /> : null
                }
              >
                {sendingMessage ? "Odesílám..." : "Odeslat zprávu"}
              </Button>
            </div>
          </TabPanel>
          <TabPanel value="3" className={styles.content}>
            <div className={styles.documents}>
              {eventDocuments.length > 0 ? (
                <div className={styles.cards}>
                  {eventDocuments.map(
                    (doc: EventDocument | string, idx: number) => {
                      const documentData =
                        typeof doc === "string"
                          ? ({
                              id: `doc-${idx}`,
                              name: `Dokument ${idx + 1}`,
                              public_link: "",
                            } as EventDocument)
                          : doc;

                      return (
                        <Card key={documentData.id} className={styles.card}>
                          <CardContent className={styles.cardContent}>
                            <h3>
                              {documentData.name || `Dokument ${idx + 1}`}
                            </h3>
                          </CardContent>
                          <CardActions className={styles.cardActions}>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<FileDownloadOutlinedIcon />}
                              href={documentData.public_link || undefined}
                              disabled={!documentData.public_link}
                            >
                              Stáhnout
                            </Button>
                          </CardActions>
                        </Card>
                      );
                    },
                  )}
                </div>
              ) : (
                <p style={{ color: "#666", fontSize: "14px" }}>
                  K této akci nejsou přiřazeny žádné dokumenty.
                </p>
              )}

              <div className={styles.documentsEditor}>
                <h4>Správa dokumentů akce</h4>
                {editMode === "documentation" ? (
                  <>
                    <div className={styles.group}>
                      <b>Vyberte dokumenty, které mají být přiřazeny k akci</b>
                      {documentsLoading ? (
                        <CircularProgress size={20} />
                      ) : documents && documents.length > 0 ? (
                        documents.map((doc) => (
                          <FormControlLabel
                            key={doc.id}
                            control={
                              <Checkbox
                                checked={(
                                  ((editedEvent as any)?.documentation ||
                                    []) as (string | number)[]
                                ).includes(doc.id)}
                                onChange={() =>
                                  handleCheckboxToggle(doc.id, "documentation")
                                }
                                name={doc.id}
                              />
                            }
                            label={doc.name}
                          />
                        ))
                      ) : (
                        <p style={{ color: "#666", fontSize: "14px" }}>
                          Organizace zatím nemá nahrané žádné dokumenty.
                        </p>
                      )}
                    </div>
                    <div className={styles.flex}>
                      <Button
                        variant="outlined"
                        onClick={() => handleEditCancel("documentation")}
                      >
                        Zrušit
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => handleEditConfirm("documentation")}
                      >
                        Potvrdit
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => handleEdit("documentation")}
                  >
                    Upravit dokumenty
                  </Button>
                )}
              </div>
            </div>
          </TabPanel>
        </TabContext>
      </div>
      <VolunteerRatingDialog
        open={ratingDialogOpen}
        volunteerName={selectedMatchForRating?.volunteerName}
        initialScore={selectedMatchForRating?.initialScore ?? 5}
        initialDidShowUp={selectedMatchForRating?.initialShowUp ?? true}
        submitting={ratingSubmitting}
        onClose={handleCloseRatingDialog}
        onSubmit={handleSubmitRating}
      />
      <Dialog
        open={dialogOpen !== ""}
        onClose={() => setDialogOpen("")}
        aria-labelledby="event-dialog-title"
        maxWidth="xl"
        className={styles.dialog}
      >
        <DialogTitle id="event-dialog-title" className={styles.dialogTitle}>
          {dialogOpen === "interests" ? "Upravit zájmy" : "Upravit dovednosti"}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <EventInterestsSkillsSection
            title=""
            items={(dialogOpen === "interests" ? interests : skills) as any}
            selectedIds={((editedEvent as any)?.[dialogOpen] || []).map(
              (id: number) => String(id),
            )}
            onToggle={(id: string) =>
              handleCheckboxToggle(id, dialogOpen as any)
            }
            editMode={true}
            type={dialogOpen === "interests" ? "interests" : "skills"}
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            onClick={() => handleEditCancel(dialogOpen)}
          >
            Zrušit
          </Button>
          <Button
            variant="contained"
            onClick={() => handleEditConfirm(dialogOpen)}
          >
            Potvrdit
          </Button>
        </DialogActions>
      </Dialog>
      {/* @ts-ignore - JS component */}
      <GalleryModal
        isOpen={gallery.modalOpen}
        onClose={handleCloseGallery}
        gallery={{ loading: gallery.loading, images: gallery.images as any }}
        selectedImages={selectedImages as any}
        onImageSelect={handleImageSelect as any}
        isImageSelected={isImageSelected as any}
        onCheckImageInEvents={handleCheckImageInEvents as any}
        maxUploadingImages={maxUploadingImages}
        onImageUpload={handleImageUpload as any}
        onAssignImages={handleAssignImages as any}
        eventsContainingImage={eventsContainingImage as any}
        onImageDelete={handleImageDelete as any}
      />
      {/* @ts-ignore - JS component */}
      <CancelEventDialog
        event={event as any}
        dialogOpen={cancelDialogOpen}
        setDialogOpen={setCancelDialogOpen}
        onSuccessCallback={() => navigate("/organisation/events")}
      />
    </>
  );
};
